import { DataTable, type ColumnDef } from '@/core/ui/components';
import { useI18n } from '@/shared/i18n';
import type { Routine, RoutineExecution } from '@yawi/types';
import { X } from 'lucide-react';
import React from 'react';
import { useExecutionList } from '../hooks/useExecutionList';
import { ExecutionStatusBadge } from './ExecutionStatusBadge';

interface Props {
  routine: Routine;
  onClose: () => void;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

const TRIGGER_LABELS: Record<string, string> = {
  schedule: 'Programada',
  manual: 'Manual',
  retry: 'Reintento',
};

export const ExecutionListModal: React.FC<Props> = ({ routine, onClose }) => {
  const { t } = useI18n();
  const { data: executions, loading, meta, setPage, search, setSearch } = useExecutionList(routine.id);

  const columns: ColumnDef<RoutineExecution>[] = [
    {
      id: 'startedAt',
      header: 'Inicio',
      cell: (row) => <span className="text-xs text-slate-600">{formatDate(row.startedAt)}</span>,
    },
    {
      id: 'triggeredBy',
      header: 'Disparado por',
      cell: (row) => (
        <span className="text-xs text-slate-500">{TRIGGER_LABELS[row.triggeredBy] ?? row.triggeredBy}</span>
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      cell: (row) => <ExecutionStatusBadge status={row.status} />,
    },
    {
      id: 'duration',
      header: 'Duración',
      cell: (row) => (
        <span className="text-xs text-slate-500">{row.durationMs != null ? `${row.durationMs}ms` : '-'}</span>
      ),
    },
    {
      id: 'resultCount',
      header: 'Registros',
      cell: (row) => (
        <span className="text-xs text-slate-500">{row.resultCount != null ? row.resultCount : '-'}</span>
      ),
    },
    {
      id: 'error',
      header: 'Error',
      cell: (row) =>
        row.errorMessage ? (
          <span className="text-xs text-red-500 max-w-xs truncate block" title={row.errorMessage}>
            {row.errorMessage}
          </span>
        ) : (
          <span className="text-xs text-slate-400">-</span>
        ),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">{t('routines.modal.executionsTitle')}</h2>
            <p className="text-sm text-slate-500">{routine.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <DataTable
            columns={columns}
            data={executions}
            keyExtractor={(e) => e.id}
            loading={loading}
            emptyMessage={t('routines.noExecutions')}
            meta={meta}
            onPageChange={setPage}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Filtrar ejecuciones..."
          />
        </div>
      </div>
    </div>
  );
};
